import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Trash2, BrainCircuit, Plus, Database, Upload, Play, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine,
} from "recharts";

// Helper: sub-model selector + GAM lambda shared across both train dialogs
function SubModelSelector({ savedModels, onChange, gamLam, onLamChange }) {
  const all = [
    { key: "lr",  label: "Linear Regression" },
    { key: "gam", label: "GAM" },
    { key: "dt",  label: "Decision Tree" },
  ];
  const toggle = (key) => {
    const next = savedModels.includes(key)
      ? savedModels.filter(k => k !== key)
      : [...savedModels, key];
    if (next.length > 0) onChange(next);
  };
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs mb-2 block">Sub-modelos a guardar</Label>
        <div className="flex flex-col gap-2">
          {all.map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={savedModels.includes(key)}
                onCheckedChange={() => toggle(key)}
                id={`sm-${key}`}
              />
              <span className="text-sm">{label}</span>
            </label>
          ))}
        </div>
        <p className="text-[11px] text-zinc-500 mt-1">Al predecir, se promedia el resultado de los sub-modelos seleccionados.</p>
      </div>
      <div>
        <Label className="text-xs block mb-1">
          GAM — λ regularización: <strong>{gamLam}</strong>
          <span className="text-zinc-400 font-normal ml-1">(mayor = más suavizado, afecta MAE/RMSE)</span>
        </Label>
        <input
          type="range" min="0.01" max="100" step="0.01"
          value={gamLam}
          onChange={(e) => onLamChange(parseFloat(e.target.value))}
          className="w-full accent-zinc-900"
        />
        <div className="flex justify-between text-[10px] text-zinc-400 mt-0.5">
          <span>0.01 (flexible)</span><span>0.6 (defecto)</span><span>100 (muy suavizado)</span>
        </div>
      </div>
    </div>
  );
}

const MODEL_KEYS = [
  { key: "lr", label: "Linear Regression" },
  { key: "gam", label: "GAM" },
  { key: "dt", label: "Decision Tree" },
];

const PART_KINDS = ["aleta", "capo", "montante", "puerta", "techo"];

const COLS_BY_KIND = {
  global: ["part_kind", "bollos", "aluminium", "price"],
  desglosado: ["part_kind", "leve", "medio", "grave", "aluminium", "price"],
};

const EMPTY_BY_KIND = {
  global: { part_kind: "aleta", bollos: 0, aluminium: 0, price: 0 },
  desglosado: { part_kind: "aleta", leve: 0, medio: 0, grave: 0, aluminium: 0, price: 0 },
};

const LABELS = {
  part_kind: "Parte",
  bollos: "Bollos",
  leve: "Leve",
  medio: "Medio",
  grave: "Grave",
  aluminium: "Aluminio (0/1)",
  price: "Precio (€)",
};

function ModelsPanel({ models, selected, onSelect, onDelete, onTrainSynthetic }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="page-card lg:col-span-1">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500">
            <Database size={12} /> Modelos entrenados
          </div>
          <Button size="sm" variant="outline" onClick={onTrainSynthetic} data-testid="btn-train-synthetic">
            <Plus size={12} className="mr-1" />Sintético
          </Button>
        </div>
        {models.length === 0 ? (
          <div className="text-sm text-zinc-400 py-6 text-center">Aún no hay modelos.</div>
        ) : (
          <ul className="divide-y divide-[var(--rule)]">
            {models.map(m => (
              <li key={m.name} className={`flex items-center justify-between py-2 cursor-pointer ${selected?.name === m.name ? "bg-zinc-50 -mx-2 px-2 rounded" : ""}`}
                  onClick={() => onSelect(m.name)} data-testid={`model-row-${m.name}`}>
                <div>
                  <div className="font-medium text-sm flex items-center gap-2">
                    {m.name}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${m.kind === "global" ? "bg-zinc-900 text-white" : "bg-zinc-200 text-zinc-700"}`}>{m.kind}</span>
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    {(m.trained_at || "").slice(0, 16).replace("T", " ")} · {m.n_samples} muestras · {m.source}
                    {m.dataset_name ? ` · ${m.dataset_name}` : ""}
                  </div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); onDelete(m.name); }} className="text-zinc-500 hover:text-red-600 p-1"><Trash2 size={14} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="lg:col-span-2 space-y-6">
        {!selected ? (
          <div className="page-card text-center text-zinc-500 py-16">
            <BrainCircuit className="mx-auto mb-3" />
            Selecciona un modelo o entrena uno desde un dataset.
          </div>
        ) : (
          <>
            <div className="page-card">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3 flex items-center justify-between">
                <span>Comparativa — {selected.name}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded ${selected.kind === "global" ? "bg-zinc-900 text-white" : "bg-zinc-200 text-zinc-700"}`}>{selected.kind}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="tbl">
                  <thead><tr><th>Modelo</th><th>MAE</th><th>MSE</th><th>RMSE</th><th>R²</th></tr></thead>
                  <tbody>
                    {MODEL_KEYS.map(({ key, label }) => {
                      const m = selected.metrics?.[key] || {};
                      return (
                        <tr key={key}>
                          <td className="font-medium">{label}</td>
                          <td>{m.mae?.toFixed(2)}</td>
                          <td>{m.mse?.toFixed(2)}</td>
                          <td>{m.rmse?.toFixed(2)}</td>
                          <td>{m.r2?.toFixed(3)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-zinc-500 mt-3">Variables: {selected.features?.join(", ")} · Origen: {selected.source}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {MODEL_KEYS.map(({ key, label }) => {
                const samples = selected.samples?.[key] || [];
                const max = Math.max(1, ...samples.map(s => Math.max(s.actual, s.predicted)));
                return (
                  <div key={key} className="page-card">
                    <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
                    <div className="text-[11px] text-zinc-400 mb-2">Predicción vs Real</div>
                    <ResponsiveContainer width="100%" height={220}>
                      <ScatterChart>
                        <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
                        <XAxis type="number" dataKey="actual" name="Real" tick={{ fontSize: 10 }} domain={[0, max]} />
                        <YAxis type="number" dataKey="predicted" name="Predicción" tick={{ fontSize: 10 }} domain={[0, max]} />
                        <Tooltip cursor={{ strokeDasharray: "3 3" }} />
                        <ReferenceLine segment={[{ x: 0, y: 0 }, { x: max, y: max }]} stroke="#c63a1f" strokeDasharray="4 2" />
                        <Scatter data={samples} fill="#0a0a0a" />
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {MODEL_KEYS.map(({ key, label }) => {
                const r = selected.residuals?.[key] || [];
                return (
                  <div key={key} className="page-card">
                    <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
                    <div className="text-[11px] text-zinc-400 mb-2">Residuos (Real − Predicción)</div>
                    <ResponsiveContainer width="100%" height={200}>
                      <ScatterChart>
                        <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
                        <XAxis type="number" dataKey="predicted" name="Predicción" tick={{ fontSize: 10 }} />
                        <YAxis type="number" dataKey="residual" name="Residuo" tick={{ fontSize: 10 }} />
                        <Tooltip cursor={{ strokeDasharray: "3 3" }} />
                        <ReferenceLine y={0} stroke="#c63a1f" strokeDasharray="4 2" />
                        <Scatter data={r} fill="#444" />
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DatasetEditor({ dataset, onBack, onChanged, onTrained }) {
  const [d, setD] = useState(dataset);
  const [row, setRow] = useState(EMPTY_BY_KIND[dataset.kind] || EMPTY_BY_KIND.desglosado);
  const [trainOpen, setTrainOpen] = useState(false);
  const [modelName, setModelName] = useState("");
  const [savedModels, setSavedModels] = useState(["lr", "gam", "dt"]);
  const [gamLam, setGamLam] = useState(0.6);
  const fileRef = useRef(null);

  useEffect(() => {
    setD(dataset);
    setRow(EMPTY_BY_KIND[dataset.kind] || EMPTY_BY_KIND.desglosado);
  }, [dataset]);

  const cols = COLS_BY_KIND[d.kind] || COLS_BY_KIND.desglosado;

  const reload = async () => {
    const r = await api.get(`/ai/datasets/${d.id}`);
    setD(r.data); onChanged?.();
  };

  const addRow = async () => {
    try {
      await api.post(`/ai/datasets/${d.id}/rows`, row);
      setRow(EMPTY_BY_KIND[d.kind]); reload(); toast.success("Fila añadida");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delRow = async (idx) => {
    try { await api.delete(`/ai/datasets/${d.id}/rows/${idx}`); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadCsv = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post(`/ai/datasets/${d.id}/upload-csv`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.added} filas añadidas`);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
    e.target.value = "";
  };

  const train = async () => {
    if (!modelName.trim()) { toast.error("Nombre del modelo requerido"); return; }
    try {
      await api.post(`/ai/datasets/${d.id}/train`, {
        model_name: modelName.trim(),
        gam_lam: gamLam,
        saved_models: savedModels,
      });
      toast.success(`Modelo "${modelName}" entrenado`);
      setTrainOpen(false); setModelName(""); setSavedModels(["lr", "gam", "dt"]); setGamLam(0.6);
      onTrained?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const renderCellInput = (col) => {
    if (col === "part_kind") {
      return (
        <Select value={row.part_kind} onValueChange={(v) => setRow({ ...row, part_kind: v })}>
          <SelectTrigger data-testid="row-part_kind"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PART_KINDS.map(k => <SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    return (
      <Input type="number" step={col === "price" ? "0.01" : "1"}
             value={row[col] ?? 0}
             onChange={(e) => setRow({ ...row, [col]: col === "price" ? parseFloat(e.target.value || "0") : parseInt(e.target.value || "0", 10) })}
             data-testid={`row-${col}`} />
    );
  };

  const rows = d.rows || [];

  return (
    <div className="space-y-6" data-testid="dataset-editor">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={onBack} className="text-xs text-zinc-500 inline-flex items-center gap-1 hover:text-zinc-900 mb-1"><ArrowLeft size={12} /> Volver a datasets</button>
          <h2 className="font-display text-2xl flex items-center gap-3">
            {d.name}
            <span className={`text-[11px] px-2 py-0.5 rounded ${d.kind === "global" ? "bg-zinc-900 text-white" : "bg-zinc-200 text-zinc-700"}`}>{d.kind}</span>
          </h2>
          <p className="text-sm text-zinc-500 mt-1">{rows.length} filas · creado {(d.created_at || "").slice(0, 10)}</p>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".csv" hidden onChange={uploadCsv} data-testid="csv-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="btn-upload-csv"><Upload size={14} className="mr-1" />Cargar CSV</Button>
          <Dialog open={trainOpen} onOpenChange={setTrainOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-train-from-dataset"><Play size={14} className="mr-1" />Entrenar</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Entrenar modelo desde "{d.name}"</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs">Nombre del modelo (interno)</Label>
                  <Input value={modelName} onChange={(e) => setModelName(e.target.value)} placeholder="p.ej. modelo-2026-a" data-testid="train-model-name" />
                  <p className="text-[11px] text-zinc-500 mt-1">Mínimo 10 filas. El modelo será de tipo <strong>{d.kind}</strong>.</p>
                </div>
                <SubModelSelector
                  savedModels={savedModels}
                  onChange={setSavedModels}
                  gamLam={gamLam}
                  onLamChange={setGamLam}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setTrainOpen(false)}>Cancelar</Button>
                <Button onClick={train} data-testid="btn-train-confirm">Entrenar modelos</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="page-card">
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Añadir fila a mano</div>
        <div className={`grid gap-2 items-end`} style={{ gridTemplateColumns: `repeat(${cols.length}, 1fr)` }}>
          {cols.map(c => (
            <div key={c}>
              <Label className="text-xs">{LABELS[c]}</Label>
              {renderCellInput(c)}
            </div>
          ))}
        </div>
        <div className="mt-3 text-right">
          <Button onClick={addRow} data-testid="btn-add-row"><Plus size={14} className="mr-1" />Añadir fila</Button>
        </div>
      </div>

      <div className="page-card">
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Filas ({rows.length})</div>
        {rows.length === 0 ? (
          <div className="text-sm text-zinc-400 py-8 text-center">Dataset vacío. Añade filas a mano o carga un CSV.</div>
        ) : (
          <div className="overflow-x-auto max-h-[50vh]">
            <table className="tbl">
              <thead><tr><th>#</th>{cols.map(c => <th key={c}>{LABELS[c]}</th>)}<th></th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td className="text-zinc-400">{i + 1}</td>
                    {cols.map(c => <td key={c} className={c === "part_kind" ? "capitalize" : ""}>{r[c]}</td>)}
                    <td className="text-right">
                      <button onClick={() => delRow(i)} className="text-zinc-500 hover:text-red-600 p-1"><Trash2 size={12} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[11px] text-zinc-500 mt-3">
          Formato CSV esperado (cabecera): <code>{cols.join(", ")}</code>. <strong>part_kind</strong> ∈ {PART_KINDS.join(", ")}.
        </p>
      </div>
    </div>
  );
}

function DatasetsPanel({ onTrained }) {
  const [datasets, setDatasets] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("desglosado");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const r = await api.get("/ai/datasets"); setDatasets(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { toast.error("Nombre requerido"); return; }
    try {
      const r = await api.post("/ai/datasets", { name: name.trim(), kind });
      setDatasets(d => [r.data, ...d]); setOpen(false); setName(""); setKind("desglosado");
      // Open the editor for the freshly created dataset
      const full = await api.get(`/ai/datasets/${r.data.id}`);
      setEditing(full.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!confirm("¿Borrar dataset?")) return;
    try { await api.delete(`/ai/datasets/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const open_ = async (id) => {
    try { const r = await api.get(`/ai/datasets/${id}`); setEditing(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (editing) {
    return <DatasetEditor dataset={editing} onBack={() => { setEditing(null); load(); }} onChanged={load} onTrained={onTrained} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-2xl">Datasets</h2>
          <p className="text-sm text-zinc-500">
            Conjuntos de datos para entrenar la IA. Hay dos tipos:
            <strong> global</strong> (sólo nº de bollos) y <strong> desglosado</strong> (leve / medio / grave).
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-new-dataset"><Plus size={14} className="mr-1" />Nuevo dataset</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuevo dataset</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Nombre</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="p.ej. histórico-2025" data-testid="ds-name" />
              </div>
              <div>
                <Label className="text-xs mb-2 block">Tipo</Label>
                <RadioGroup value={kind} onValueChange={setKind} className="grid grid-cols-2 gap-3" data-testid="ds-kind">
                  <label className={`border rounded-lg p-3 cursor-pointer ${kind === "global" ? "border-zinc-900 bg-zinc-50" : "border-[var(--rule)]"}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <RadioGroupItem value="global" id="kind-global" />
                      <span className="text-sm font-medium">Global</span>
                    </div>
                    <p className="text-[11px] text-zinc-500">Sólo número total de bollos.</p>
                  </label>
                  <label className={`border rounded-lg p-3 cursor-pointer ${kind === "desglosado" ? "border-zinc-900 bg-zinc-50" : "border-[var(--rule)]"}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <RadioGroupItem value="desglosado" id="kind-desg" />
                      <span className="text-sm font-medium">Desglosado</span>
                    </div>
                    <p className="text-[11px] text-zinc-500">Bollos por severidad (leve/medio/grave).</p>
                  </label>
                </RadioGroup>
              </div>
              <p className="text-[11px] text-zinc-500">
                En ambos tipos la <strong>parte</strong> es la variable categórica (aleta, montante, techo, puerta, capó). Ambos montantes se calculan igual.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={create} data-testid="btn-create-dataset">Crear</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="page-card overflow-x-auto">
        <table className="tbl">
          <thead><tr><th>Nombre</th><th>Tipo</th><th>Filas</th><th>Creado</th><th></th></tr></thead>
          <tbody>
            {datasets.map(d => (
              <tr key={d.id} className="cursor-pointer" onClick={() => open_(d.id)} data-testid={`dataset-row-${d.id}`}>
                <td className="font-medium">{d.name}</td>
                <td>
                  <span className={`text-[11px] px-2 py-0.5 rounded ${d.kind === "global" ? "bg-zinc-900 text-white" : "bg-zinc-200 text-zinc-700"}`}>{d.kind}</span>
                </td>
                <td>{d.row_count}</td>
                <td>{(d.created_at || "").slice(0, 10)}</td>
                <td className="text-right">
                  <button onClick={(e) => { e.stopPropagation(); del(d.id); }} className="text-zinc-500 hover:text-red-600 p-1"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {datasets.length === 0 && <tr><td colSpan="5" className="text-center text-zinc-400 py-10">Aún no hay datasets.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function IAPage() {
  const [models, setModels] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("desglosado");
  const [nSamples, setNSamples] = useState(600);
  const [busy, setBusy] = useState(false);
  const [savedModels, setSavedModels] = useState(["lr", "gam", "dt"]);
  const [gamLam, setGamLam] = useState(0.6);

  const loadModels = async () => {
    try {
      const r = await api.get("/ai/models");
      setModels(r.data);
      if (selected) {
        const fresh = r.data.find(m => m.name === selected.name);
        if (fresh) setSelected(fresh); else setSelected(null);
      } else if (r.data.length) {
        setSelected(r.data[0]);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadModels(); /* eslint-disable-next-line */ }, []);

  const trainSynthetic = async () => {
    if (!name.trim()) { toast.error("Nombre requerido"); return; }
    setBusy(true);
    try {
      const r = await api.post("/ai/train", {
        name: name.trim(),
        kind,
        n_samples: Number(nSamples) || 600,
        gam_lam: gamLam,
        saved_models: savedModels,
      });
      toast.success(`Modelo "${r.data.name}" entrenado (${r.data.kind})`);
      setOpen(false); setName(""); setNSamples(600); setKind("desglosado"); setSavedModels(["lr", "gam", "dt"]); setGamLam(0.6);
      await loadModels();
      const fresh = await api.get(`/ai/models/${encodeURIComponent(r.data.name)}`);
      setSelected(fresh.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const del = async (n) => {
    if (!confirm(`¿Borrar modelo ${n}?`)) return;
    try { await api.delete(`/ai/models/${encodeURIComponent(n)}`); loadModels(); if (selected?.name === n) setSelected(null); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const select = async (n) => {
    try { const r = await api.get(`/ai/models/${encodeURIComponent(n)}`); setSelected(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="ia-page">
      <div className="mb-8">
        <h1 className="section-title">Inteligencia (IA)</h1>
        <p className="text-sm text-zinc-500 mt-1">Crea conjuntos de datos, entrena modelos LR/GLM/Decision Tree y compáralos.</p>
      </div>

      <Tabs defaultValue="models" className="space-y-6">
        <TabsList data-testid="ia-tabs">
          <TabsTrigger value="models" data-testid="tab-models">Modelos</TabsTrigger>
          <TabsTrigger value="datasets" data-testid="tab-datasets">Datasets</TabsTrigger>
        </TabsList>

        <TabsContent value="models">
          <ModelsPanel
            models={models}
            selected={selected}
            onSelect={select}
            onDelete={del}
            onTrainSynthetic={() => setOpen(true)}
          />
        </TabsContent>

        <TabsContent value="datasets">
          <DatasetsPanel onTrained={loadModels} />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Entrenar con datos sintéticos</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nombre del modelo (interno)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="p.ej. quilles-2026-a" data-testid="train-name" />
            </div>
            <div>
              <Label className="text-xs mb-2 block">Tipo</Label>
              <RadioGroup value={kind} onValueChange={setKind} className="grid grid-cols-2 gap-3">
                <label className={`border rounded-lg p-3 cursor-pointer ${kind === "global" ? "border-zinc-900 bg-zinc-50" : "border-[var(--rule)]"}`}>
                  <div className="flex items-center gap-2"><RadioGroupItem value="global" /><span className="text-sm font-medium">Global</span></div>
                </label>
                <label className={`border rounded-lg p-3 cursor-pointer ${kind === "desglosado" ? "border-zinc-900 bg-zinc-50" : "border-[var(--rule)]"}`}>
                  <div className="flex items-center gap-2"><RadioGroupItem value="desglosado" /><span className="text-sm font-medium">Desglosado</span></div>
                </label>
              </RadioGroup>
            </div>
            <div>
              <Label className="text-xs">Nº de muestras sintéticas</Label>
              <Input type="number" min="50" value={nSamples} onChange={(e) => setNSamples(e.target.value)} />
              <p className="text-[11px] text-zinc-500 mt-1">El nombre del modelo no aparece nunca en presupuestos ni facturas.</p>
            </div>
            <SubModelSelector
              savedModels={savedModels}
              onChange={setSavedModels}
              gamLam={gamLam}
              onLamChange={setGamLam}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={trainSynthetic} disabled={busy} data-testid="btn-train-submit">{busy ? "Entrenando…" : "Entrenar modelos"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
